import { loadRegionalPresentation } from "app/client/lib/RegionalPresentation";
import { AppModel, reportError, TopAppModel } from "app/client/models/AppModel";
import { urlState } from "app/client/models/gristUrlState";
import { RegionalCapabilities } from "app/common/UserAPI";

import { Observable, subscribe } from "grainjs";

const DEFAULT_REGIONAL_CAPABILITIES: RegionalCapabilities = {
  enabled: true,
  role: null,
  canValidate: false,
  canEditStructure: false,
};

type RegionalCapsProvider = AppModel | TopAppModel;

interface RegionalCapsEntry {
  obs: Observable<RegionalCapabilities>;
  lastSignature: string | null;
}

const _regionalCapsByApp = new WeakMap<object, RegionalCapsEntry>();

function _getRegionalCapsCacheKey(provider: RegionalCapsProvider): object {
  return "topAppModel" in provider ? provider.topAppModel : provider;
}

function _getTopAppModel(provider: RegionalCapsProvider): TopAppModel {
  return "topAppModel" in provider ? provider.topAppModel : provider;
}

function _getRegionalCapsSignature(provider: RegionalCapsProvider): string {
  const topAppModel = _getTopAppModel(provider);
  const currentApp = topAppModel.appObs.get();
  const currentUser = currentApp?.currentUser;
  const state = urlState().state.get();
  return [
    state.org || "",
    state.doc || "",
    state.slug || "",
    currentUser?.email || currentUser?.loginEmail || "",
  ].join("|");
}

const _REGIONAL_CAPS_MAX_ATTEMPTS = 3;

function _loadRegionalCapabilities(
  provider: RegionalCapsProvider,
  obs: Observable<RegionalCapabilities>,
  attempt: number = 0,
) {
  const docId = urlState().state.get().doc || undefined;
  provider.api.getRegionalCapabilities({docId})
    .then((caps) => obs.set(caps))
    .catch((err) => {
      // Retry transient failures with backoff before falling back to the restrictive
      // default, so a flaky request does not silently lock an admin out of structure
      // editing for the whole session. The fallback still fails closed (canEditStructure:false).
      if (attempt + 1 < _REGIONAL_CAPS_MAX_ATTEMPTS) {
        setTimeout(() => _loadRegionalCapabilities(provider, obs, attempt + 1), 1000 * (attempt + 1));
        return;
      }
      reportError(err);
      obs.set(DEFAULT_REGIONAL_CAPABILITIES);
    });
}

export function getRegionalCapabilitiesObs(provider: RegionalCapsProvider): Observable<RegionalCapabilities> {
  const key = _getRegionalCapsCacheKey(provider);
  let entry = _regionalCapsByApp.get(key);
  if (!entry) {
    const obs = Observable.create(null, DEFAULT_REGIONAL_CAPABILITIES);
    entry = {
      obs,
      lastSignature: null,
    };
    _regionalCapsByApp.set(key, entry);
    // Похідна презентація колонок вантажиться разом зі здатностями: обидві — регіональні дані
    // з сервера, і обидві потрібні з першого рендера. Помилка тут не фатальна — інтерфейс
    // працює на метаданих документа, як і досі. [provision-scope, етап 3]
    void loadRegionalPresentation();
    const topAppModel = _getTopAppModel(provider);
    const initialSignature = _getRegionalCapsSignature(provider);
    entry.lastSignature = initialSignature;
    _loadRegionalCapabilities(provider, entry.obs);
    subscribe(urlState().state, topAppModel.appObs, () => {
      if (!entry) {
        return;
      }
      const nextSignature = _getRegionalCapsSignature(provider);
      if (entry.lastSignature === nextSignature) {
        return;
      }
      entry.lastSignature = nextSignature;
      _loadRegionalCapabilities(provider, entry.obs);
    });
  }
  return entry.obs;
}

export function isRegionalStructureEditor(caps: RegionalCapabilities): boolean {
  return !caps.enabled || caps.canEditStructure;
}

export function isRegionalValidationAvailable(caps: RegionalCapabilities): boolean {
  return Boolean(caps.enabled && caps.canValidate);
}
